import React, { useEffect, useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast, Toaster } from "sonner";
import { ArrowRight, Building2, Briefcase, CheckCircle2 } from "lucide-react";
import ThemeToggle from "@/components/ThemeToggle";
import api from "@/lib/api";
import { applyAccent } from "@/lib/theme";

export default function Login() {
  const nav = useNavigate();
  const { login, switchOrg } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  // Org-picker step state (shown only if user is a member of >1 orgs)
  const [pickOrgs, setPickOrgs] = useState(null);       // array | null
  const [pickBusy, setPickBusy] = useState(false);

  // Iter-195 · Branded invite link — `/login?tenant=<org_id or slug>` fetches
  // the tenant's logo + accent from the public /api/public/tenant-brand
  // endpoint and applies them so the whole page adopts the brand.
  const [searchParams] = useSearchParams();
  const brandParam = searchParams.get("tenant") || searchParams.get("brand") || "";
  const [brand, setBrand] = useState(null);
  useEffect(() => {
    if (!brandParam) return;
    const q = /^[0-9a-f-]{8,}$/i.test(brandParam) ? { id: brandParam } : { slug: brandParam };
    api.get("/public/tenant-brand", { params: q })
      .then((r) => { if (r.data?.found) { setBrand(r.data); if (r.data.accent_hex) applyAccent(r.data.accent_hex); } })
      .catch(() => {});
  }, [brandParam]);

  const routeForOrg = (org) => (org?.is_agency ? "/agency/dashboard" : "/app/dashboard");

  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const data = await login(email, password);
      if (data.needs_org_select && (data.orgs || []).length > 1) {
        setPickOrgs(data.orgs);          // stay on page, show picker
        toast.info(`Pick an organization to continue (${data.orgs.length} available).`);
      } else {
        toast.success("Welcome back");
        nav(routeForOrg(data.org));
      }
    } catch (err) {
      // Iter-116 · make login errors actually helpful
      const detail = err?.response?.data?.detail;
      const status = err?.response?.status;
      if (status === 401 && detail === "Invalid credentials") {
        toast.error(`No account matches ${email}. Check the email spelling or create a new company below.`, { duration: 6000 });
      } else if (status === 403) {
        toast.error(detail || "Your account is not a member of this organization.");
      } else if (status === 429) {
        toast.error("Too many login attempts. Wait 60 seconds and try again.");
      } else {
        toast.error(detail || err?.message || "Login failed — please try again");
      }
    } finally { setBusy(false); }
  };

  const chooseOrg = async (o) => {
    setPickBusy(true);
    try {
      await switchOrg(o.id);
      toast.success(`Signed in to ${o.name}`);
      nav(o.is_agency ? "/agency/dashboard" : "/app/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not switch org");
    } finally { setPickBusy(false); }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2 dark bg-background text-foreground">
      <Toaster theme="dark" richColors />
      <div className="hidden md:block relative overflow-hidden bg-black">
        <div className="absolute inset-0 opacity-40" style={{ background: "radial-gradient(circle at 20% 20%, rgba(37,99,235,0.25), transparent 45%), radial-gradient(circle at 80% 30%, rgba(168,85,247,0.22), transparent 50%), radial-gradient(circle at 60% 80%, rgba(249,115,22,0.18), transparent 50%)" }} />
        <div className="relative z-10 p-10 lg:p-14 h-full flex flex-col justify-between">
          <Link to="/" className="flex items-center gap-3">
            {brand?.logo_base64 ? (
              <img src={brand.logo_base64} alt={brand.name || "Company"} className="h-12 w-auto max-w-[160px] object-contain bg-white/5 rounded-sm p-1" data-testid="brand-logo" />
            ) : (
              <img src="/zenith360-logo.png" alt="Zenith 360" className="h-10 w-auto" />
            )}
            <div>
              <div className="font-heading font-bold text-lg text-logo-gradient" data-testid="brand-name">
                {brand?.name || "Zenith 360 ERP"}
              </div>
              <div className="text-[9px] font-mono uppercase tracking-widest text-white/60">
                {brand?.name ? "Powered by Zenith 360" : "India · AI-native"}
              </div>
            </div>
          </Link>
          <div>
            <h1 className="font-heading font-bold text-4xl lg:text-5xl leading-[1.05] tracking-tight text-white">
              Welcome<br /><span className="text-logo-gradient">back{brand?.name ? " to " + brand.name.split(" ")[0] : ""}.</span>
            </h1>
            <div className="text-[11px] font-mono uppercase tracking-widest text-white/60 mt-6">GL · Sub-Ledgers · AI · GST · Schedule II</div>
          </div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-white/50">v1.4 · India · Finance-grade</div>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 md:p-10 bg-background relative">
        <div className="absolute top-4 right-4 z-10">
          <ThemeToggle variant="compact" />
        </div>
        {!pickOrgs && (
          <form onSubmit={submit} className="w-full max-w-sm space-y-6" data-testid="login-form">
            <div className="space-y-1.5">
              <div className="text-[11px] font-mono uppercase tracking-widest text-accent">// Sign in</div>
              <h2 className="font-heading text-3xl font-bold tracking-tight">Welcome back</h2>
              <div className="text-sm text-foreground/70">New here? <Link to="/signup" className="text-accent hover:underline">Create a company</Link>.</div>
            </div>
            <div className="space-y-3">
              <div>
                <Label htmlFor="email" className="text-[10px] font-mono uppercase tracking-widest text-foreground/60">Email</Label>
                <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                       className="rounded-sm mt-1.5" data-testid="login-email" />
              </div>
              <div>
                <Label htmlFor="password" className="text-[10px] font-mono uppercase tracking-widest text-foreground/60">Password</Label>
                <Input id="password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                       className="rounded-sm mt-1.5" data-testid="login-password" />
              </div>
            </div>
            <Button type="submit" disabled={busy} className="w-full rounded-sm bg-logo-gradient hover:opacity-95 text-white font-medium border-0 h-11" data-testid="login-submit">
              {busy ? "Signing in…" : "Sign in"}
            </Button>
            <div className="flex items-center justify-between text-[11px] pt-1">
              <Link to="/forgot-password" className="text-accent hover:underline" data-testid="login-forgot-link">Forgot password?</Link>
              <span className="text-foreground/40 font-mono">Emails via Resend · TLS</span>
            </div>
            <div className="text-[10px] text-foreground/50 text-center">One email can belong to many companies — you'll pick after signing in.</div>
          </form>
        )}

        {pickOrgs && (
          <div className="w-full max-w-md space-y-5" data-testid="org-picker">
            <div className="space-y-1.5">
              <div className="text-[11px] font-mono uppercase tracking-widest text-accent">// Choose organization</div>
              <h2 className="font-heading text-3xl font-bold tracking-tight">Which company?</h2>
              <div className="text-sm text-foreground/70">You are a member of {pickOrgs.length} organizations. Pick one to continue — you can switch anytime from the topbar.</div>
            </div>
            <div className="space-y-2 max-h-[60vh] overflow-y-auto pr-1">
              {pickOrgs.map((o) => (
                <button key={o.id} onClick={() => chooseOrg(o)} disabled={pickBusy}
                  data-testid={`org-pick-${o.id}`}
                  className={`w-full text-left rounded-sm border p-4 transition-all disabled:opacity-50 ${o.is_active ? "border-accent bg-accent/10" : "border-border hover:border-accent/40 bg-card"}`}>
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-sm flex items-center justify-center shrink-0 ${o.is_agency ? "bg-accent/15 text-accent" : "bg-muted"}`}>
                      {o.is_agency ? <Briefcase className="w-5 h-5" /> : <Building2 className="w-5 h-5 text-foreground/60" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-heading font-semibold truncate">{o.name}</div>
                      <div className="text-[10px] font-mono uppercase tracking-widest text-foreground/60 mt-0.5 flex items-center gap-2">
                        {o.is_agency ? "Agency panel" : `Plan · ${o.plan || "—"}`}
                        {o.role && <span>· {o.role}</span>}
                        {o.is_active && <span className="text-accent">· current</span>}
                      </div>
                    </div>
                    <ArrowRight className="w-4 h-4 text-foreground/40" />
                  </div>
                </button>
              ))}
            </div>
            <button onClick={() => setPickOrgs(null)} className="text-xs text-foreground/60 hover:text-foreground" data-testid="org-picker-back">← Use a different email</button>
          </div>
        )}
      </div>
    </div>
  );
}
